import { useSyncExternalStore } from "react";

/**
 * The code version the API most recently reported on one of its responses.
 *
 * Learned inside an axios interceptor, so it lives outside React and is
 * subscribed to with `useSyncExternalStore`: a subscriber re-renders when a
 * deploy changes the answer mid-session.
 */
let serverVersion: string | undefined;

const listeners = new Set<() => void>();

export const rememberServerVersion = (version: string | undefined): void => {
  // A response without the header says nothing about the version; keep the last
  // one known.
  if (version === undefined || version === serverVersion) {
    return;
  }

  serverVersion = version;
  for (const listener of listeners) {
    listener();
  }
};

/** Forgets what was observed, so one story or test cannot leak into the next. */
export const resetServerVersion = (): void => {
  serverVersion = undefined;
  for (const listener of listeners) {
    listener();
  }
};

export const getServerVersion = (): string | undefined => serverVersion;

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useServerVersion = (): string | undefined =>
  useSyncExternalStore(subscribe, getServerVersion);
